import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, Field, PageHeader, Pager, StatusBadge } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtRelative } from "@/lib/format";

export const userStatus = (u) => (u.locked ? { s: "bloqueado", l: "Bloqueado" } : u.status === "activo" ? { s: "activo", l: "Activo" } : { s: "inactivo", l: "Inactivo" });

const CreateUserDialog = ({ open, onOpenChange, roles, onCreated, canRoles }) => {
  const empty = { name: "", email: "", username: "", password: "", role_ids: [] };
  const [f, setF] = useState(empty);
  const toggle = (id) => setF({ ...f, role_ids: f.role_ids.includes(id) ? f.role_ids.filter((r) => r !== id) : [...f.role_ids, id] });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/users", { ...f, username: f.username || null });
      toast.success("Usuario creado. Deberá cambiar su contraseña al ingresar.");
      setF(empty);
      onCreated(data);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="create-user-dialog">
        <DialogHeader><DialogTitle>Nuevo usuario</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nombre completo"><Input data-testid="new-user-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
            <Field label="Usuario (opcional)"><Input data-testid="new-user-username-input" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} /></Field>
          </div>
          <Field label="Email"><Input data-testid="new-user-email-input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required /></Field>
          <Field label="Contraseña temporal" hint="Mín. 10 caracteres con letras y números. Se exigirá cambiarla en el primer ingreso.">
            <Input data-testid="new-user-password-input" type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required />
          </Field>
          {canRoles && (
            <div>
              <div className="text-[13px] font-semibold mb-2">Roles</div>
              <div className="grid sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto rounded-lg border border-[#e4e8e1] p-3">
                {roles.filter((r) => r.status === "activo").map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox data-testid={`new-user-role-${r.code}`} checked={f.role_ids.includes(r.id)} onCheckedChange={() => toggle(r.id)} />{r.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          <DialogFooter><Button data-testid="new-user-submit-btn" className="rounded-full">Crear usuario</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const UserRow = ({ u, onOpen }) => {
  const st = userStatus(u);
  return (
    <tr data-testid={`user-row-${u.id}`} onClick={onOpen} className="cursor-pointer border-b border-[#f0f2ee] hover:bg-[#f6fbf2] transition-colors">
      <td className="px-5 py-3"><div className="font-semibold">{u.name}</div><div className="text-xs text-[#5b6656]">{u.email}</div></td>
      <td className="px-5 py-3 font-mono text-xs">{u.username || "—"}</td>
      <td className="px-5 py-3 text-xs">{u.roles.map((r) => r.name).join(", ") || <span className="text-[#b42318]">Sin rol</span>}</td>
      <td className="px-5 py-3"><StatusBadge status={st.s} label={st.l} /></td>
      <td className="px-5 py-3 text-xs text-[#5b6656]">{fmtRelative(u.last_login_at)}</td>
    </tr>
  );
};

const UserCard = ({ u, onOpen }) => {
  const st = userStatus(u);
  return (
    <button data-testid={`user-card-${u.id}`} onClick={onOpen} className="w-full text-left px-4 py-3 border-b border-[#f0f2ee]">
      <div className="flex items-center justify-between gap-2"><span className="font-semibold">{u.name}</span><StatusBadge status={st.s} label={st.l} /></div>
      <div className="text-xs text-[#5b6656]">{u.email}</div>
      <div className="text-xs mt-1">{u.roles.map((r) => r.name).join(", ") || "Sin rol"}</div>
    </button>
  );
};

export default function Users() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ q: "", status: "all", page: 1 });
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/users", { params: { q: filters.q, status: filters.status === "all" ? "" : filters.status, page: filters.page } }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (can("admin.roles.ver")) api.get("/admin/roles").then((r) => setRoles(r.data)); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="users-page">
      <PageHeader eyebrow="Administración" title="Usuarios" subtitle="Los usuarios con historial no se eliminan: se desactivan para mantener la trazabilidad."
        actions={can("admin.usuarios.crear") && <Button data-testid="create-user-btn" className="rounded-full" onClick={() => setOpen(true)}><Plus size={16} className="mr-1" />Nuevo usuario</Button>} />
      <div className="syc-card overflow-hidden">
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-[#e4e8e1]">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a9584]" />
            <Input data-testid="users-search-input" placeholder="Buscar por nombre, email o usuario" className="pl-9"
              value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value, page: 1 })} />
          </div>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v, page: 1 })}>
            <SelectTrigger data-testid="users-status-filter" className="sm:w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="activo">Activos</SelectItem>
              <SelectItem value="inactivo">Inactivos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {data.items.length === 0 ? <EmptyState title="No se encontraron usuarios" /> : (
          <>
            <table className="hidden md:table w-full text-sm" data-testid="users-table">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-[#5b6656] bg-[#fafbf9]">
                <th className="px-5 py-3">Usuario</th><th className="px-5 py-3">Login</th><th className="px-5 py-3">Roles</th><th className="px-5 py-3">Estado</th><th className="px-5 py-3">Último ingreso</th>
              </tr></thead>
              <tbody>{data.items.map((u) => <UserRow key={u.id} u={u} onOpen={() => navigate(`/admin/usuarios/${u.id}`)} />)}</tbody>
            </table>
            <div className="md:hidden">{data.items.map((u) => <UserCard key={u.id} u={u} onOpen={() => navigate(`/admin/usuarios/${u.id}`)} />)}</div>
          </>
        )}
        <Pager page={data.page} total={data.total} pageSize={data.page_size} onPage={(p) => setFilters({ ...filters, page: p })} />
      </div>
      <CreateUserDialog open={open} onOpenChange={setOpen} roles={roles} canRoles={can("admin.roles.gestionar")}
        onCreated={(u) => { setOpen(false); navigate(`/admin/usuarios/${u.id}`); }} />
    </div>
  );
}
